import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";

import "../../assets/css/product-studio-edit.css";
import "../../assets/css/print-center.css";
import NotificationComponent from "../../components/Alert/alert";
import {
  useFetchLabelDemandSummaryQuery,
  useFetchPrintPresetsQuery,
  useLazyFetchPrintJobQuery,
  useResolvePrintItemsMutation,
  useResolveReceiptLabelsMutation,
} from "../../api/printingApi";
import { useFetchCompanyUserDataQuery } from "../../api/usersApi";
import { useTranslation } from "../../hooks/useTranslation";
import { Btn, Icon, Notice, Spinner, cx } from "../catalogProductStudio/edit/StudioUi";
import PrintCatalogView from "./catalog/PrintCatalogView";
import PendingPrintsBanner from "./confirm/PendingPrintsBanner";
import PrintConfirmationBar from "./confirm/PrintConfirmation";
import PrintHistoryView from "./history/PrintHistoryView";
import PrintJobDrawer from "./history/PrintJobDrawer";
import NeedsLabelsView from "./needs/NeedsLabelsView";
import PresetSettingsDrawer from "./presets/PresetSettingsDrawer";
import { readPresetChoice, savePresetChoice } from "./presets/deviceCalibration";
import PrintReviewDialog from "./review/PrintReviewDialog";
import {
  SELECTION_ORIGINS,
  buildJobLines,
  lineQuantity,
  resolveJobSource,
  resolveReprintOfJobId,
} from "./state/printSelectionModel";
import { planReceiptMerge } from "./state/receiptLinkModel";
import { RECEIPT_PARAM, parseReceiptParam } from "./printCenterLinks";
import {
  applyBulk,
  applyReceiptMerge,
  clearSelection,
  deselectItems,
  refreshItems,
  restoreSelection,
  selectItems,
  selectPrintSelection,
  setLineQuantities,
} from "./state/printSelectionSlice";
import { loadStoredSelection, saveStoredSelection } from "./state/printSelectionStorage";
import PrintTray from "./tray/PrintTray";
import { PRESET_ADMIN_ROLES, fill, getApiErrorDetails, plural, readTokenScope } from "./printCenterModel";

const VIEWS = ["needs-labels", "catalog", "history"];

/**
 * Print Center: its own workspace for printing labels. It reads products and
 * stock but never manages them — that stays in Products.
 */
function PrintCenter() {
  const t = useTranslation();
  const tt = useCallback((key) => t(`printCenter.${key}`), [t]);
  const dispatch = useDispatch();
  const selection = useSelector(selectPrintSelection);
  const [params, setParams] = useSearchParams();
  const notificationRef = useRef(null);
  const notify = useCallback((message, type = "success") => notificationRef.current?.showNotification(message, type), []);

  const { data: companyUser } = useFetchCompanyUserDataQuery();
  const tokenScope = useMemo(() => readTokenScope(), []);
  const role = String(companyUser?.user?.role || tokenScope.role || "").toLowerCase();
  const scope = useMemo(
    () => ({
      companyId:
        companyUser?.user?.company_id ?? companyUser?.company?.id ?? tokenScope.companyId ?? null,
      userId: companyUser?.user?.id ?? tokenScope.userId ?? null,
    }),
    [companyUser, tokenScope]
  );

  const {
    data: presets = [],
    isLoading: isLoadingPresets,
    isError: presetsFailed,
    isFetching: isFetchingPresets,
    refetch: refetchPresets,
  } = useFetchPrintPresetsQuery();
  const [presetChoice, setPresetChoice] = useState(() => readPresetChoice());
  const activePreset = presets.find((preset) => preset.id === presetChoice) || presets.find((preset) => preset.isDefault) || presets[0];
  const { data: demandSummary } = useFetchLabelDemandSummaryQuery();

  const requestedView = params.get("view");
  const view = VIEWS.includes(requestedView)
    ? requestedView
    : demandSummary && demandSummary.outstanding === 0
    ? "catalog"
    : "needs-labels";
  const setView = (next) => {
    const nextParams = new URLSearchParams(params);
    nextParams.set("view", next);
    setParams(nextParams);
  };

  const [review, setReview] = useState(null);
  const [confirmJob, setConfirmJob] = useState(null);
  const [openJobId, setOpenJobId] = useState(null);
  const [presetDrawerOpen, setPresetDrawerOpen] = useState(false);
  const [trayOpen, setTrayOpen] = useState(false);
  const [lastPrinted, setLastPrinted] = useState(null);
  const [resolveItems] = useResolvePrintItemsMutation();
  const [resolveReceiptLabels] = useResolveReceiptLabelsMutation();
  const [fetchJob] = useLazyFetchPrintJobQuery();

  // ── Session persistence: restore, then revalidate against the server ──────
  const hydratedRef = useRef(false);
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    if (hydratedRef.current || !scope.companyId || !scope.userId) return;
    hydratedRef.current = true;
    setHydrated(true);
    const stored = loadStoredSelection(scope);
    const keys = stored?.order?.length ? stored.order : selection.order;
    if (stored?.order?.length && !selection.order.length) dispatch(restoreSelection(stored));
    if (!keys.length) return;
    resolveItems({ keys })
      .unwrap()
      .then((result) => {
        dispatch(refreshItems(result));
        if (result.missingKeys?.length) notify(plural(tt, "unavailableAfterRefresh", result.missingKeys.length), "warning");
      })
      .catch(() => {});
  }, [dispatch, notify, resolveItems, scope, selection.order, tt]);

  useEffect(() => {
    if (!hydratedRef.current) return;
    saveStoredSelection(scope, selection);
  }, [scope, selection]);

  // ── "Print X labels" links: ?receipt=<id>[,<id>] ──────────────────────────
  // Resolved on the server (current quantities, not the ones at link time),
  // merged into the tray without duplicates, then removed from the URL so a
  // refresh does not apply it again (and if it did, the merge is idempotent).
  const receiptParam = params.get(RECEIPT_PARAM);
  const [openingReceipts, setOpeningReceipts] = useState(false);
  const handledReceiptParamRef = useRef(null);
  const selectionRef = useRef(selection);
  selectionRef.current = selection;
  useEffect(() => {
    if (!hydrated || !receiptParam || handledReceiptParamRef.current === receiptParam) return;
    handledReceiptParamRef.current = receiptParam;
    const receiptIds = parseReceiptParam(receiptParam);
    const nextParams = new URLSearchParams(params);
    nextParams.delete(RECEIPT_PARAM);
    if (!nextParams.get("view")) nextParams.set("view", "needs-labels");
    if (!receiptIds.length) {
      setParams(nextParams, { replace: true });
      return;
    }
    setOpeningReceipts(true);
    resolveReceiptLabels(receiptIds)
      .unwrap()
      .then((result) => {
        const plan = planReceiptMerge(selectionRef.current, result);
        if (plan.updates.length) dispatch(applyReceiptMerge(plan.updates));
        reportReceiptMerge(plan);
        if (plan.added > 0) setTrayOpen(true);
      })
      .catch(() => notify(tt("receiptLoadError"), "danger"))
      .finally(() => {
        setOpeningReceipts(false);
        setParams(nextParams, { replace: true });
      });
    // Runs once per receipt link.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrated, receiptParam]);

  const receiptLabel = (receipts) =>
    receipts.length === 1
      ? fill(tt(`receiptName_${receipts[0].kind}`), { id: receipts[0].id })
      : fill(tt("receiptsName"), { count: receipts.length });

  const reportReceiptMerge = (plan) => {
    if (plan.notFound.length) {
      notify(fill(tt(plan.notFound.length === 1 ? "receiptNotFoundOne" : "receiptNotFoundOther"), { ids: plan.notFound.join(", #") }), "warning");
    }
    const receipts = plan.receipts;
    if (!receipts.length) return;
    const name = receiptLabel(receipts);
    const total = (field) => receipts.reduce((sum, receipt) => sum + receipt[field], 0);
    const reasons = [];
    if (total("settled")) reasons.push(fill(tt("receiptReasonPrinted"), { count: total("settled") }));
    if (total("dismissed")) reasons.push(fill(tt("receiptReasonDismissed"), { count: total("dismissed") }));
    const holders = new Map();
    receipts.forEach((receipt) =>
      receipt.pendingJobs.forEach((job) => {
        const current = holders.get(job.jobId) || { ...job, quantity: 0 };
        current.quantity += job.quantity;
        holders.set(job.jobId, current);
      })
    );
    holders.forEach((job) =>
      reasons.push(fill(tt("receiptReasonPending"), { count: job.quantity, job: job.jobId, name: job.createdByName || "—" }))
    );
    // "Nothing left to print from Restock #2 (3 already printed)."
    const withWhy = (sentence) => (reasons.length ? `${sentence.replace(/\.$/, "")} (${reasons.join(" · ")}).` : sentence);

    if (receipts.every((receipt) => receipt.status === "not_needed")) {
      notify(fill(tt("receiptNotNeeded"), { receipt: name }), "info");
      return;
    }
    if (plan.alreadyInTray) {
      notify(fill(tt("receiptAlreadyInTray"), { receipt: name }), "info");
      return;
    }
    if (!plan.added) {
      notify(withWhy(fill(tt("receiptNothingLeft"), { receipt: name })), "info");
      return;
    }
    const merged = plan.trayHadOtherLabels ? ` ${fill(tt("receiptMerged"), { count: plan.trayHadOtherLabels })}` : "";
    if (receipts.some((receipt) => receipt.status === "partial" || receipt.status === "nothing_left")) {
      notify(`${withWhy(fill(tt("receiptPartial"), { count: total("outstanding"), required: total("required"), receipt: name }))}${merged}`, "warning");
      return;
    }
    notify(`${plural(tt, "receiptAdded", plan.added, { receipt: name })}${merged}`, "success");
  };

  // ── Unconfirmed prints: confirm from the banner ───────────────────────────
  const confirmJobById = (jobId) => {
    fetchJob(jobId)
      .unwrap()
      .then((job) => {
        if (job.status === "awaiting_confirmation") setConfirmJob(job);
        else setOpenJobId(jobId);
      })
      .catch((error) => notify(getApiErrorDetails(error).message || tt("actionError"), "danger"));
  };

  // A prepared selection survives in-app navigation and refresh; closing the
  // tab is the one way to lose it, so that asks first.
  const hasSelection = selection.order.length > 0;
  useEffect(() => {
    if (!hasSelection) return undefined;
    const handler = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasSelection]);

  // ── Selection actions ─────────────────────────────────────────────────────
  const onSelect = useCallback(
    (items, origin = SELECTION_ORIGINS.CATALOG) => dispatch(selectItems({ items, origin })),
    [dispatch]
  );
  const onDeselect = useCallback((keys) => dispatch(deselectItems(keys)), [dispatch]);
  // Needs labels: a day, product, receipt or variant, as that receipt's new stock.
  const onSelectReceipts = useCallback(
    (result) => {
      const plan = planReceiptMerge(selectionRef.current, result);
      if (plan.updates.length) dispatch(applyReceiptMerge(plan.updates));
    },
    [dispatch]
  );

  const openReview = () => {
    const { entries, order } = selection;
    const lines = order
      .map((key) => entries[key])
      .filter((entry) => entry && lineQuantity(entry) > 0)
      .map((entry) => ({ item: entry.item, quantity: lineQuantity(entry), newStock: entry.newStock }));
    setReview({
      type: "new",
      lines,
      jobLines: buildJobLines(entries, order),
      source: resolveJobSource(entries, order),
      reprintOfJobId: resolveReprintOfJobId(entries, order),
    });
    setTrayOpen(false);
  };

  const openAttempt = (job, lines) => {
    const usable = lines.filter((entry) => entry.line.current && !entry.line.current.blockedReason);
    if (!usable.length) {
      notify(tt("printAgainUnavailable"), "warning");
      return;
    }
    setReview({
      type: "attempt",
      job,
      lines: usable.map(({ line, quantity }) => ({ item: line.current, quantity, newStock: Math.min(line.newStockQuantity, quantity) })),
      lineIds: lines.length === job.lines.length ? null : usable.map(({ line }) => line.id),
    });
    setOpenJobId(null);
  };

  const handlePrinted = ({ job, mode }) => {
    setReview(null);
    if (mode.type === "new") {
      setLastPrinted({ jobId: job.id, snapshot: selection });
      dispatch(clearSelection());
    }
    notify(fill(tt(job.requiresConfirmation ? "sentAwaiting" : "sentToPrint"), { job: job.id }), "success");
    if (job.requiresConfirmation) setConfirmJob(job);
  };

  const resolveConflict = (conflicts, action) => {
    conflicts.forEach((conflict) => {
      const entry = selection.entries[conflict.key];
      if (!entry) return;
      if (action === "use_available") {
        dispatch(setLineQuantities({ key: conflict.key, newStock: conflict.available }));
      } else {
        dispatch(setLineQuantities({ key: conflict.key, newStock: 0, extra: Number(entry.extra) + Number(entry.newStock) }));
      }
    });
    resolveItems({ keys: selection.order })
      .unwrap()
      .then((result) => dispatch(refreshItems(result)))
      .catch(() => {});
  };

  const addFromJob = (job, lines, origin) => {
    dispatch(
      selectItems({
        items: lines.map((line) => line.current),
        origin,
        reprintOfJobId: origin === SELECTION_ORIGINS.REPRINT ? job.id : null,
        quantities: Object.fromEntries(lines.map((line) => [line.current.key, { newStock: 0, extra: line.quantity }])),
      })
    );
    setOpenJobId(null);
    setTrayOpen(true);
    notify(plural(tt, origin === SELECTION_ORIGINS.REPRINT ? "reprintAdded" : "duplicateAdded", lines.length), "success");
  };

  const isPresetAdmin = PRESET_ADMIN_ROLES.includes(role);
  const counts = { "needs-labels": demandSummary?.outstanding || 0 };

  return (
    <div className={cx("content pse pc", hasSelection && "has-tray-selection")}>
      <NotificationComponent ref={notificationRef} />

      <header className="pc-page-head">
        <div>
          <p className="pc-eyebrow">
            <Icon name="printer" size={14} /> {tt("eyebrow")}
          </p>
          <h1 className="pc-page-head__title">{tt("pageTitle")}</h1>
          <p className="pc-page-head__subtitle">{tt("pageSubtitle")}</p>
        </div>
        <button type="button" className="pc-preset-chip" onClick={() => setPresetDrawerOpen(true)} disabled={!activePreset}>
          <Icon name="grid" size={16} />
          <span>
            <span className="pc-preset-chip__label">{tt("presetLabel")}</span>
            <strong>{activePreset ? activePreset.name : tt("loading")}</strong>
          </span>
          {activePreset ? (
            <span className="pc-preset-chip__meta">{fill(tt("presetShort"), { columns: activePreset.config.sheet.columns, rows: activePreset.config.sheet.rows })}</span>
          ) : null}
        </button>
      </header>

      <nav className="pc-tabs" role="tablist" aria-label={tt("pageTitle")}>
        {VIEWS.map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={view === key}
            className={cx("pc-tab", view === key && "is-active")}
            onClick={() => setView(key)}
          >
            <Icon name={key === "needs-labels" ? "tag" : key === "catalog" ? "search" : "history"} size={16} />
            {tt(`tab_${key.replace("-", "_")}`)}
            {counts[key] ? <span className="pc-tab__count">{counts[key]}</span> : null}
          </button>
        ))}
      </nav>

      {lastPrinted ? (
        <div className="pc-last-printed">
          <Icon name="checkCircle" size={16} />
          <span>{fill(tt("lastPrinted"), { job: lastPrinted.jobId })}</span>
          <button type="button" className="pc-link" onClick={() => setOpenJobId(lastPrinted.jobId)}>{tt("viewJob")}</button>
          <button
            type="button"
            className="pc-link"
            onClick={() => {
              dispatch(restoreSelection(lastPrinted.snapshot));
              setLastPrinted(null);
            }}
          >
            {tt("restoreSelection")}
          </button>
          <button type="button" className="pse-icon-btn" aria-label={tt("close")} onClick={() => setLastPrinted(null)}>
            <Icon name="x" size={14} />
          </button>
        </div>
      ) : null}

      <PendingPrintsBanner
        tt={tt}
        jobs={demandSummary?.awaitingConfirmation}
        userId={scope.userId}
        hiddenJobId={confirmJob?.id}
        onOpenJob={setOpenJobId}
        onConfirmJob={confirmJobById}
      />

      {!isLoadingPresets && (presetsFailed || !presets.length) ? (
        <Notice
          tone="danger"
          title={tt("presetLoadErrorTitle")}
          action={
            <Btn size="sm" icon="refresh" disabled={isFetchingPresets} onClick={() => refetchPresets()}>
              {tt("retry")}
            </Btn>
          }
        >
          {tt("presetLoadErrorCopy")}
        </Notice>
      ) : null}

      {openingReceipts ? (
        <div className="pc-receipt-loading" role="status">
          <Spinner /> {tt("receiptLoading")}
        </div>
      ) : null}

      <div className="pc-workspace">
        <main className="pc-main" role="tabpanel">
          {isLoadingPresets ? (
            <div className="pc-loading"><Spinner label={tt("loading")} /></div>
          ) : view === "needs-labels" ? (
            <NeedsLabelsView
              tt={tt}
              entries={selection.entries}
              onSelect={onSelect}
              onSelectReceipts={onSelectReceipts}
              onDeselect={onDeselect}
              onOpenJob={setOpenJobId}
              onBrowseCatalog={() => setView("catalog")}
              notify={notify}
            />
          ) : view === "catalog" ? (
            <PrintCatalogView tt={tt} entries={selection.entries} onSelect={onSelect} onDeselect={onDeselect} notify={notify} />
          ) : (
            <PrintHistoryView tt={tt} onOpenJob={setOpenJobId} />
          )}
        </main>

        <PrintTray
          tt={tt}
          selection={selection}
          slotsPerPage={activePreset?.slotsPerPage}
          onSetQuantities={(payload) => dispatch(setLineQuantities(payload))}
          onRemove={onDeselect}
          onClear={() => dispatch(clearSelection())}
          onBulk={(action) => dispatch(applyBulk(action))}
          onReview={openReview}
          isMobileOpen={trayOpen}
          onToggleMobile={() => setTrayOpen((value) => !value)}
        />
      </div>

      {review && presets.length ? (
        <PrintReviewDialog
          tt={tt}
          mode={review}
          presets={presets}
          presetId={activePreset?.id}
          onChangePreset={(id) => {
            setPresetChoice(id);
            savePresetChoice(id);
          }}
          isPresetAdmin={isPresetAdmin}
          onOpenPresetSettings={() => setPresetDrawerOpen(true)}
          onClose={() => setReview(null)}
          onPrinted={handlePrinted}
          onResolveConflict={resolveConflict}
          onOpenJob={setOpenJobId}
          onRemoveKeys={onDeselect}
        />
      ) : null}

      {confirmJob ? (
        <PrintConfirmationBar
          tt={tt}
          job={confirmJob}
          notify={notify}
          onDone={() => setConfirmJob(null)}
          onHide={() => {
            setConfirmJob(null);
            notify(tt("confirmLaterNote"), "info");
          }}
          onPrintFailedAgain={(job, lines) => {
            setConfirmJob(null);
            openAttempt(job, lines);
          }}
        />
      ) : null}

      <PrintJobDrawer
        tt={tt}
        jobId={openJobId}
        onClose={() => setOpenJobId(null)}
        onPrintAgain={(job) => openAttempt(job, job.lines.map((line) => ({ line, quantity: line.quantity })))}
        onReprint={(job, lines) => addFromJob(job, lines, SELECTION_ORIGINS.REPRINT)}
        onDuplicate={(job, lines) => addFromJob(job, lines, SELECTION_ORIGINS.CATALOG)}
        onAwaitConfirmation={(job) => {
          setOpenJobId(null);
          setConfirmJob(job);
        }}
      />

      {activePreset ? (
        <PresetSettingsDrawer
          tt={tt}
          isOpen={presetDrawerOpen}
          onClose={() => setPresetDrawerOpen(false)}
          preset={activePreset}
          presets={presets}
          onSelectPreset={(id) => {
            if (!id) return;
            setPresetChoice(id);
            savePresetChoice(id);
          }}
          canEdit={isPresetAdmin}
          notify={notify}
        />
      ) : null}

      <span className="pc-sr-only" aria-live="polite">
        {hasSelection ? plural(tt, "labelCount", selection.order.reduce((sum, key) => sum + lineQuantity(selection.entries[key]), 0)) : ""}
      </span>
    </div>
  );
}

export default PrintCenter;
